import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "../../services/toast";

import { makeStyles } from 'tss-react/mui';
import { green } from "@mui/material/colors";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import Box from "@mui/material/Box";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import CodeIcon from "@mui/icons-material/Code";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CampaignIcon from "@mui/icons-material/SendOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import BadgeIcon from "@mui/icons-material/Badge";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import TuneIcon from "@mui/icons-material/Tune";
import CallSplitIcon from "@mui/icons-material/CallSplit";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { isPromptOfType, promptOptionLabel, promptsOfType } from "../../helpers/promptTypes";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import SelectDeliverySpeedMessage from "../../components/SelectDeliverySpeedMessage";
import SelectOperationalState from "../../components/SelectOperationalState";
import ConnectionModalSidebar from "../ConnectionModalSidebar";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },

  cuerpo: {
    display: "flex",
    height: "min(560px, 68vh)",
  },

  panel: {
    flex: 1,
    minWidth: 0,
    overflowY: "auto",
    padding: theme.spacing(2, 2.5),
    ...theme.scrollbarStyles,
  },

  section: {
    padding: theme.spacing(1.5, 0),
    "&::before": {
      display: "none",
    },
  },

  accordionSummary: {
    padding: 0,
    minHeight: "auto",
    "&.Mui-expanded": {
      minHeight: "auto",
    },
    "& .MuiAccordionSummary-content": {
      margin: 0,
      "&.Mui-expanded": {
        margin: 0,
      },
    },
  },

  accordionDetails: {
    padding: theme.spacing(1.5, 0, 0),
  },

  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    fontSize: "0.95rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },

  sectionTitleIcon: {
    fontSize: 19,
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },

  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },

  switchLabelBlock: {
    display: "flex",
    flexDirection: "column",
  },

  switchLabel: {
    margin: 0,
    "& .MuiFormControlLabel-label": {
      fontSize: "0.9rem",
      fontWeight: 600,
    },
  },

  switchDescription: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(6),
    marginTop: 2,
  },

  dialogTitle: {
    paddingBottom: theme.spacing(0),
    "& h2": {
      fontSize: "1.15rem",
      fontWeight: 600,
    },
  },

  dialogContent: {
    padding: 0,
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const { classes } = useStyles();

  const initialState = {
    name: "",
    number: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    iscampaings: false,
    isFallback: false,
    marketingApiEnabled: false,
    token: "",
    provider: "beta",
    timeSendQueue: 0,
    sendIdQueue: 0,
    expiresInactiveMessage: "",
    expiresTicket: 0,
    timeUseBotQueues: 0,
    maxUseBotQueues: 3,
    state: "new",
    deliverySpeedMessage: 0,
  };

  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [tab, setTab] = useState(0);
  const [messagesExpanded, setMessagesExpanded] = useState(false);

  useEffect(() => {
    setMessagesExpanded(
      Boolean(
        whatsApp.greetingMessage ||
        whatsApp.complationMessage ||
        whatsApp.outOfHoursMessage ||
        whatsApp.ratingMessage
      )
    );
  }, [whatsApp]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);
        setWhatsApp(data);
        data.promptId ? setSelectedPrompt(data.promptId) : setSelectedPrompt(null);
        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  const handleSaveWhatsApp = async (values) => {
    if (selectedQueueIds.length === 0 && selectedPrompt == null) {
      return toastError("Debe selecionar al menos 1 departamento!");
    }

    const whatsappData = {
      ...values,
      queueIds: selectedQueueIds,
      promptId: selectedPrompt ? selectedPrompt : null,
    };

    delete whatsappData["queues"];
    delete whatsappData["session"];

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
    setSelectedQueueIds([]);
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
    setTab(0);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle className={classes.dialogTitle}>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers className={classes.dialogContent}>
                <Box className={classes.cuerpo}>
                  <ConnectionModalSidebar
                    tabs={[
                      { value: 0, label: i18n.t("whatsappModal.tabs.general"), icon: <SettingsIcon fontSize="small" /> },
                      { value: 1, label: i18n.t("whatsappModal.tabs.api"), icon: <CodeIcon fontSize="small" /> },
                      { value: 2, label: i18n.t("whatsappModal.tabs.bot"), icon: <SmartphoneIcon fontSize="small" /> },
                      { value: 3, label: i18n.t("whatsappModal.tabs.campaigns"), icon: <CampaignIcon fontSize="small" /> },
                    ]}
                    active={tab}
                    onChange={setTab}
                  />
                  <div className={classes.panel}>
                {tab === 0 && (
                  <Box>
                    {}
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        <BadgeIcon className={classes.sectionTitleIcon} />
                        {i18n.t("whatsappModal.sections.identification")}
                      </Typography>
                      <Grid container spacing={2} sx={{
                        alignItems: "center"
                      }}>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 6
                          }}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            size="small"
                            fullWidth
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            sm: 6
                          }}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.number")}
                            name="number"
                            error={touched.number && Boolean(errors.number)}
                            helperText={touched.number && errors.number}
                            variant="outlined"
                            margin="dense"
                            size="small"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{
                        mt: 1
                      }} className={classes.switchRow}>
                        <FormControlLabel
                          className={classes.switchLabel}
                          control={
                            <Field
                              as={Switch}
                              color="primary"
                              name="isDefault"
                              checked={values.isDefault}
                            />
                          }
                          label={i18n.t("whatsappModal.form.default")}
                        />
                        <Chip
                          size="small"
                          label={values.isDefault ? i18n.t("whatsappModal.form.active") : i18n.t("whatsappModal.form.inactive")}
                          color={values.isDefault ? "primary" : "default"}
                          variant={values.isDefault ? "filled" : "outlined"}
                        />
                      </Box>
                      <Typography className={classes.switchDescription}>
                        {i18n.t("whatsappModal.form.defaultDesc")}
                      </Typography>
                    </div>

                    <Divider />

                    {}
                    <Accordion
                      elevation={0}
                      disableGutters
                      expanded={messagesExpanded}
                      onChange={(e, isExpanded) => setMessagesExpanded(isExpanded)}
                      className={classes.section}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="small" />}
                        className={classes.accordionSummary}
                      >
                        <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                          <ChatBubbleOutlineIcon className={classes.sectionTitleIcon} />
                          {i18n.t("whatsappModal.sections.autoMessages")}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails className={classes.accordionDetails}>
                      <Grid container spacing={2}>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueModal.form.greetingMessage")}
                            multiline
                            rows={3}
                            fullWidth
                            name="greetingMessage"
                            error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                            helperText={touched.greetingMessage && errors.greetingMessage}
                            variant="outlined"
                            margin="dense"
                            size="small"
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueModal.form.complationMessage")}
                            multiline
                            rows={3}
                            fullWidth
                            name="complationMessage"
                            error={touched.complationMessage && Boolean(errors.complationMessage)}
                            helperText={touched.complationMessage && errors.complationMessage}
                            variant="outlined"
                            margin="dense"
                            size="small"
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 12
                          }}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueModal.form.outOfHoursMessage")}
                            multiline
                            rows={3}
                            fullWidth
                            name="outOfHoursMessage"
                            error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
                            helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
                            variant="outlined"
                            margin="dense"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                      </AccordionDetails>
                    </Accordion>

                    <Divider />

                    {}
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        <AccountTreeIcon className={classes.sectionTitleIcon} />
                        {i18n.t("whatsappModal.sections.routing")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" gutterBottom sx={{ display: "block" }}>
                        {i18n.t("whatsappModal.form.routingDesc")}
                      </Typography>
                      <QueueSelect
                        selectedQueueIds={selectedQueueIds}
                        onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                      />
                      <Box sx={{
                        mt: 1
                      }}>
                        <FormControl margin="dense" size="small" variant="outlined" fullWidth>
                          <InputLabel>
                            {i18n.t("whatsappModal.form.prompt")}
                          </InputLabel>
                          <Select
                            labelId="dialog-select-prompt-label"
                            id="dialog-select-prompt"
                            name="promptId"
                            value={selectedPrompt || ""}
                            onChange={handleChangePrompt}
                            label={i18n.t("whatsappModal.form.prompt")}
                            fullWidth
                            MenuProps={{
                              anchorOrigin: { vertical: "bottom", horizontal: "left" },
                              transformOrigin: { vertical: "top", horizontal: "left" },
                              getContentAnchorEl: null,
                            }}
                          >
                            {promptsOfType(prompts, "text", selectedPrompt).map((prompt) => (
                              <MenuItem key={prompt.id} value={prompt.id} disabled={!isPromptOfType(prompt, "text")}>
                                {promptOptionLabel(prompt, "text")}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </div>
                  </Box>
                )}

                {}
                {tab === 1 && (
                  <Box sx={{
                    pt: 2
                  }}>
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        <VpnKeyIcon className={classes.sectionTitleIcon} />
                        {i18n.t("whatsappModal.sections.token")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" gutterBottom sx={{ display: "block" }}>
                        {i18n.t("whatsappModal.form.tokenDesc")}
                      </Typography>
                      <Field
                        as={TextField}
                        label={i18n.t("queueModal.form.token")}
                        fullWidth
                        name="token"
                        variant="outlined"
                        margin="dense"
                        size="small"
                      />
                    </div>

                    <Divider />

                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        <TuneIcon className={classes.sectionTitleIcon} />
                        {i18n.t("whatsappModal.sections.operationalState")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" gutterBottom sx={{ display: "block" }}>
                        {i18n.t("whatsappModal.form.operationalStateDesc")}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <SelectOperationalState
                            value={values.state}
                            onChange={(state) => setFieldValue("state", state)}
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <SelectDeliverySpeedMessage
                            value={values.deliverySpeedMessage}
                            onChange={(ms) => setFieldValue("deliverySpeedMessage", ms)}
                          />
                        </Grid>
                      </Grid>
                    </div>
                  </Box>
                )}

                {}
                {tab === 2 && (
                  <Box sx={{
                    pt: 2
                  }}>
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        <CallSplitIcon className={classes.sectionTitleIcon} />
                        {i18n.t("whatsappModal.form.queueRedirection")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" gutterBottom sx={{
                        display: "block"
                      }}>
                        {i18n.t("whatsappModal.form.queueRedirectionDesc")}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.maxUseBotQueues")}
                            fullWidth
                            name="maxUseBotQueues"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            error={touched.maxUseBotQueues && Boolean(errors.maxUseBotQueues)}
                            helperText={touched.maxUseBotQueues && errors.maxUseBotQueues}
                          />
                        </Grid>
                        <Grid
                          size={{
                            xs: 12,
                            md: 6
                          }}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.timeUseBotQueues")}
                            fullWidth
                            name="timeUseBotQueues"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            error={touched.timeUseBotQueues && Boolean(errors.timeUseBotQueues)}
                            helperText={touched.timeUseBotQueues && errors.timeUseBotQueues}
                          />
                        </Grid>
                        <Grid size={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.expiresTicket")}
                            fullWidth
                            name="expiresTicket"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            error={touched.expiresTicket && Boolean(errors.expiresTicket)}
                            helperText={touched.expiresTicket && errors.expiresTicket}
                          />
                        </Grid>
                        <Grid size={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("whatsappModal.form.expiresInactiveMessage")}
                            multiline
                            rows={3}
                            fullWidth
                            name="expiresInactiveMessage"
                            error={touched.expiresInactiveMessage && Boolean(errors.expiresInactiveMessage)}
                            helperText={touched.expiresInactiveMessage && errors.expiresInactiveMessage}
                            variant="outlined"
                            margin="dense"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </div>
                  </Box>
                )}

                {}
                {tab === 3 && (
                  <Box sx={{
                    pt: 2
                  }}>
                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        <CampaignIcon className={classes.sectionTitleIcon} />
                        {i18n.t("whatsappModal.sections.bulk")}
                      </Typography>
                      <div>
                        <div className={classes.switchRow}>
                          <FormControlLabel
                            className={classes.switchLabel}
                            control={
                              <Field
                                as={Switch}
                                color="primary"
                                name="iscampaings"
                                checked={values.iscampaings}
                              />
                            }
                            label={i18n.t("whatsappModal.form.bulkEnabled")}
                          />
                          <Chip
                            size="small"
                            label={values.iscampaings ? i18n.t("whatsappModal.form.active") : i18n.t("whatsappModal.form.inactive")}
                            color={values.iscampaings ? "primary" : "default"}
                            variant={values.iscampaings ? "filled" : "outlined"}
                          />
                        </div>
                        <Typography className={classes.switchDescription}>
                          {i18n.t("whatsappModal.form.bulkEnabledDesc")}
                        </Typography>
                      </div>
                    </div>

                    <Divider />

                    <div className={classes.section}>
                      <Typography className={classes.sectionTitle}>
                        <SwapHorizIcon className={classes.sectionTitleIcon} />
                        {i18n.t("whatsappModal.sections.backupLine")}
                      </Typography>
                      <div>
                        <div className={classes.switchRow}>
                          <FormControlLabel
                            className={classes.switchLabel}
                            control={
                              <Field
                                as={Switch}
                                color="primary"
                                name="isFallback"
                                checked={values.isFallback}
                              />
                            }
                            label={i18n.t("whatsappModal.form.backupLineEnabled")}
                          />
                          <Chip
                            size="small"
                            label={values.isFallback ? i18n.t("whatsappModal.form.active") : i18n.t("whatsappModal.form.inactive")}
                            color={values.isFallback ? "primary" : "default"}
                            variant={values.isFallback ? "filled" : "outlined"}
                          />
                        </div>
                        <Typography className={classes.switchDescription}>
                          {i18n.t("whatsappModal.form.backupLineDesc")}
                        </Typography>
                      </div>

                      <div>
                        <div className={classes.switchRow}>
                          <FormControlLabel
                            className={classes.switchLabel}
                            control={
                              <Field
                                as={Switch}
                                color="primary"
                                name="marketingApiEnabled"
                                checked={values.marketingApiEnabled}
                                disabled={values.channel !== "whatsapp_cloud"}
                              />
                            }
                            label="API de Mensajes de marketing"
                          />
                          <Chip
                            size="small"
                            label={values.marketingApiEnabled ? "Activa" : "Inactiva"}
                            color={values.marketingApiEnabled ? "primary" : "default"}
                            variant={values.marketingApiEnabled ? "filled" : "outlined"}
                          />
                        </div>
                        <Typography className={classes.switchDescription}>
                          {values.channel === "whatsapp_cloud"
                            ? "Las plantillas de MARKETING salen por la ruta optimizada de Meta, que declara hasta un 9% más de entregas. Las de utilidad y autenticación no cambian. Requiere que la cuenta esté ONBOARDED; si Meta la rechaza, el envío se reintenta por la ruta normal."
                            : "Solo disponible en conexiones de WhatsApp Cloud."}
                        </Typography>
                      </div>
                    </div>
                  </Box>
                )}
                  </div>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
